import { Button, Group, Loader, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import { z } from 'zod';

import { Batch, BatchBrokerStatusEnum, BatchStatusEnum } from '@e-origin/shared';

import { useChangeBatchStatus } from '@admin/hooks';
import { Select } from '@components';
import { useSettings } from '@hooks/useSettings';

interface ChangeBatchStatusProps {
  onClose: () => void;
  batch: Batch;
}

const changeBatchStatusSchema = z.object({
  status: z.object({
    value: z.string(),
    details: z.string().nullish(),
    subStatus: z.string().nullish(),
    brokerStatus: z.string().nullish(),
  }),
});

export const ChangeBatchStatus: React.FC<ChangeBatchStatusProps> = ({ onClose, batch }) => {
  const { data: settings } = useSettings();
  const changeBatchStatusMutation = useChangeBatchStatus();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      status: {
        value: batch.status.value,
        details: batch.status.details || null,
        subStatus: batch.status.subStatus || null,
        brokerStatus: batch.status.brokerStatus || null,
      },
    },
    validate: zodResolver(changeBatchStatusSchema),
    validateInputOnBlur: true,
  });

  const statusOptions = Object.keys(BatchStatusEnum).map((key) => ({
    value: BatchStatusEnum[key],
    label: BatchStatusEnum[key],
  }));

  const brokerStatusOptions = Object.keys(BatchBrokerStatusEnum).map((key) => ({
    value: BatchBrokerStatusEnum[key],
    label: BatchBrokerStatusEnum[key],
  }));

  const handleSubmit = async (values: typeof form.values) => {
    setFormError(null);
    try {
      await changeBatchStatusMutation.mutateAsync({
        status: values.status,
        batchId: batch._id,
      });
      onClose();
    } catch (error: any) {
      setFormError(error?.response?.data?.message || 'Failed to update batch status');
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md" mt="xl">
        <Select label="Status" placeholder="Select status" data={statusOptions} {...form.getInputProps('status.value')} searchable />

        {settings.isForwarder && batch.broker && (
          <Select
            label="Broker Status"
            placeholder="Select broker status (optional)"
            data={brokerStatusOptions}
            {...form.getInputProps('status.brokerStatus')}
            searchable
            clearable
            allowDeselect
          />
        )}

        {formError && (
          <Text c="red" fw="bold">
            {formError}
          </Text>
        )}
        <Group justify="center" mt="md">
          <Button type="button" onClick={onClose} disabled={changeBatchStatusMutation.isPending} variant="transparent">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={changeBatchStatusMutation.isPending || !form.isValid()}
            leftSection={changeBatchStatusMutation.isPending && <Loader color="white" size="xs" />}
          >
            Change Status
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
